import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const TYPE_OPTIONS = ['local', 'mobile', 'toll-free', 'national', 'fmc'];
const CURRENCY_OPTIONS = ['USD', 'UAH', 'EUR'];

const EMPTY_FORM = {
  country: '',
  city: '',
  type: 'local',
  provider: '',
  code: '',
  channels: '',
  activation_fee: '',
  monthly_fee: '',
  internal_activation_fee: '',
  internal_monthly_fee: '',
  currency: 'USD',
  incoming_cost: '',
  required_documents: '',
  comment: '',
  is_from_price: false,
};

const PhoneCatalogForm = ({ open, onClose, onSave, entry }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(entry?.id);

  useEffect(() => {
    if (entry) {
      setForm({
        country: entry.country || '',
        city: entry.city || '',
        type: entry.type || 'local',
        provider: entry.provider || '',
        code: entry.code || '',
        channels: entry.channels || '',
        activation_fee: entry.activation_fee ?? '',
        monthly_fee: entry.monthly_fee ?? '',
        internal_activation_fee: entry.internal_activation_fee ?? '',
        internal_monthly_fee: entry.internal_monthly_fee ?? '',
        currency: entry.currency || 'USD',
        incoming_cost: entry.incoming_cost || '',
        required_documents: entry.required_documents || '',
        comment: entry.comment || '',
        is_from_price: entry.is_from_price || false,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [entry, open]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        activation_fee: form.activation_fee !== '' ? Number(form.activation_fee) : 0,
        monthly_fee: form.monthly_fee !== '' ? Number(form.monthly_fee) : 0,
        internal_activation_fee: form.internal_activation_fee !== '' ? Number(form.internal_activation_fee) : null,
        internal_monthly_fee: form.internal_monthly_fee !== '' ? Number(form.internal_monthly_fee) : null,
      };
      await onSave(payload, entry?.id);
      onClose();
    } catch {
      // error handled by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isEdit
          ? t('phoneCatalog.editEntry', 'Edit Catalog Entry')
          : t('phoneCatalog.addEntry', 'Add Catalog Entry')}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('phoneCatalog.country', 'Country')}
              value={form.country}
              onChange={handleChange('country')}
              size="small"
              fullWidth
            />
            <TextField
              label={t('phoneCatalog.city', 'City')}
              value={form.city}
              onChange={handleChange('city')}
              size="small"
              fullWidth
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('phoneCatalog.type', 'Type')}</InputLabel>
              <Select
                value={form.type}
                onChange={handleChange('type')}
                label={t('phoneCatalog.type', 'Type')}
              >
                {TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('phoneCatalog.provider', 'Provider')}
              value={form.provider}
              onChange={handleChange('provider')}
              size="small"
              fullWidth
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('phoneCatalog.code', 'Code')}
              value={form.code}
              onChange={handleChange('code')}
              size="small"
              fullWidth
            />
            <TextField
              label={t('phoneCatalog.channels', 'Channels')}
              value={form.channels}
              onChange={handleChange('channels')}
              size="small"
              fullWidth
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('phoneCatalog.activationFee', 'Activation Fee')}
              value={form.activation_fee}
              onChange={handleChange('activation_fee')}
              size="small"
              type="number"
              fullWidth
            />
            <TextField
              label={t('phoneCatalog.monthlyFee', 'Monthly Fee')}
              value={form.monthly_fee}
              onChange={handleChange('monthly_fee')}
              size="small"
              type="number"
              fullWidth
            />
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>{t('phoneCatalog.currency', 'Currency')}</InputLabel>
              <Select
                value={form.currency}
                onChange={handleChange('currency')}
                label={t('phoneCatalog.currency', 'Currency')}
              >
                {CURRENCY_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('phoneCatalog.internalActivationFee', 'Int. Activation Fee')}
              value={form.internal_activation_fee}
              onChange={handleChange('internal_activation_fee')}
              size="small"
              type="number"
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            />
            <TextField
              label={t('phoneCatalog.internalMonthlyFee', 'Int. Monthly Fee')}
              value={form.internal_monthly_fee}
              onChange={handleChange('internal_monthly_fee')}
              size="small"
              type="number"
              fullWidth
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            />
          </Box>

          <TextField
            label={t('phoneCatalog.incomingCost', 'Incoming Cost')}
            value={form.incoming_cost}
            onChange={handleChange('incoming_cost')}
            size="small"
            fullWidth
          />

          <TextField
            label={t('phoneCatalog.requiredDocuments', 'Required Documents')}
            value={form.required_documents}
            onChange={handleChange('required_documents')}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />

          <TextField
            label={t('phoneCatalog.comment', 'Comment')}
            value={form.comment}
            onChange={handleChange('comment')}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={form.is_from_price}
                onChange={handleChange('is_from_price')}
                sx={{ '&.Mui-checked': { color: '#00C853' } }}
              />
            }
            label={t('phoneCatalog.isFromPrice', 'Is "FROM" price')}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !form.country}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: '#00C853',
            '&:hover': { bgcolor: '#00A843' },
          }}
        >
          {saving ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : isEdit ? (
            t('common.save', 'Save')
          ) : (
            t('common.create', 'Create')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhoneCatalogForm;
